"use client";

import React, { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { FileUp, X, File } from "lucide-react";

/**
 * CDropzone - A file upload drag & drop component
 *
 * Supports single and multiple file uploads with drag & drop functionality,
 * file type validation, and size limits.
 *
 * @example
 * <CDropzone
 *   name="documents"
 *   label="Upload Documents"
 *   accept=".pdf,.doc,.docx"
 *   maxSize={10 * 1024 * 1024}
 *   multiple={true}
 *   value={files}
 *   onChange={setFiles}
 *   hint="PDF, DOC up to 10MB"
 * />
 */

export interface CDropzoneProps {
  /** Name attribute for the input */
  name: string;
  /** Label displayed above the dropzone */
  label?: React.ReactNode;
  /** Accepted file types (e.g., ".pdf,.doc,.jpg,.png") */
  accept?: string;
  /** Maximum file size in bytes (default: 10MB) */
  maxSize?: number;
  /** Whether to allow multiple files */
  multiple?: boolean;
  /** Currently selected file(s) */
  value?: File | File[] | null;
  /** Callback when files change */
  onChange?: (files: File | File[] | null) => void;
  /** Whether the dropzone is disabled */
  disabled?: boolean;
  /** Array of error messages (from form validation) */
  error?: string[];
  /** Whether to show error styling */
  errorQuery?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Placeholder text shown in the dropzone */
  placeholder?: string;
  /** Hint text shown below the dropzone */
  hint?: string;
  /** Whether a file is required */
  required?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
}

const CDropzone: React.FC<CDropzoneProps> = ({
  name,
  label,
  accept,
  maxSize = 10 * 1024 * 1024, // 10MB default
  multiple = false,
  value,
  onChange,
  disabled = false,
  error,
  errorQuery = false,
  errorMessage = "Please upload a file",
  placeholder = "Drag & drop files here, or click to browse",
  hint,
  required = false,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  // Derive error state
  const hasError = errorQuery || (error && error.length > 0) || !!internalError;
  const displayError = internalError || error?.[0] || errorMessage;

  // Get current files as array for display
  const currentFiles: File[] = value
    ? Array.isArray(value)
      ? value
      : [value]
    : [];

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Validate files
  const validateFiles = useCallback(
    (files: FileList | File[]): File[] => {
      const validFiles: File[] = [];
      setInternalError(null);

      const fileArray = Array.from(files);

      for (const file of fileArray) {
        // Check file size
        if (file.size > maxSize) {
          setInternalError(
            `File "${file.name}" exceeds maximum size of ${formatFileSize(maxSize)}`,
          );
          continue;
        }

        // Check file type if accept is specified
        if (accept) {
          const acceptedTypes = accept
            .split(",")
            .map((t) => t.trim().toLowerCase());
          const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
          const fileMimeType = file.type.toLowerCase();

          const isAccepted = acceptedTypes.some(
            (type) =>
              type === fileExtension ||
              type === fileMimeType ||
              (type.endsWith("/*") &&
                fileMimeType.startsWith(type.replace("/*", "/"))),
          );

          if (!isAccepted) {
            setInternalError(`File type "${fileExtension}" is not accepted`);
            continue;
          }
        }

        validFiles.push(file);
      }

      return validFiles;
    },
    [accept, maxSize],
  );

  // Handle file selection
  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      if (disabled) return;

      const validFiles = validateFiles(files);

      if (validFiles.length === 0) return;

      if (multiple) {
        // For multiple, append to existing or set new
        const newFiles = [...currentFiles, ...validFiles];
        onChange?.(newFiles);
      } else {
        // For single, replace
        onChange?.(validFiles[0] || null);
      }
    },
    [disabled, multiple, currentFiles, onChange, validateFiles],
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = "";
  };

  // Handle drag events
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // Handle remove file
  const handleRemoveFile = (index: number) => {
    if (disabled) return;

    if (multiple) {
      const newFiles = currentFiles.filter((_, i) => i !== index);
      onChange?.(newFiles.length > 0 ? newFiles : null);
    } else {
      onChange?.(null);
    }
    setInternalError(null);
  };

  // Handle click to open file dialog
  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  // Handle keyboard interaction
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <label
          htmlFor={name}
          className="text-sm text-muted-foreground mb-2 block"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Dropzone area */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={placeholder}
        aria-disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          // Base styles
          "relative flex flex-col items-center justify-center",
          "border-2 border-dashed rounded-xl p-6",
          "transition-all duration-200 cursor-pointer",
          "min-h-[140px]",
          // Default state
          !isDragging &&
            !hasError && [
              "border-violet-200 bg-violet-50/30",
              "hover:border-violet-400 hover:bg-violet-50/50",
              "dark:border-violet-500/30 dark:bg-violet-500/5",
              "dark:hover:border-violet-400 dark:hover:bg-violet-500/10",
            ],
          // Dragging state
          isDragging && [
            "border-violet-500 bg-violet-100/50",
            "dark:border-violet-400 dark:bg-violet-500/20",
          ],
          // Error state
          hasError && [
            "border-red-300 bg-red-50/30",
            "dark:border-red-500/50 dark:bg-red-500/5",
          ],
          // Disabled state
          disabled && [
            "opacity-50 cursor-not-allowed",
            "hover:border-violet-200 hover:bg-violet-50/30",
            "dark:hover:border-violet-500/30 dark:hover:bg-violet-500/5",
          ],
        )}
      >
        <input
          ref={inputRef}
          type="file"
          id={name}
          name={name}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={handleInputChange}
          className="sr-only"
          aria-describedby={hasError ? `${name}-error` : undefined}
        />

        <FileUp
          className={cn(
            "w-10 h-10 mb-3",
            isDragging
              ? "text-violet-500 dark:text-violet-400"
              : "text-violet-400 dark:text-violet-500",
          )}
        />

        <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
          {placeholder}
        </p>

        {hint && (
          <p className="text-xs text-slate-500 dark:text-slate-500 mt-1 text-center">
            {hint}
          </p>
        )}
      </div>

      {/* File list */}
      {currentFiles.length > 0 && (
        <div className="mt-3 space-y-2">
          {currentFiles.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className={cn(
                "flex items-center justify-between",
                "px-3 py-2 rounded-lg",
                "bg-slate-100 dark:bg-slate-800",
                "border border-slate-200 dark:border-slate-700",
              )}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <File className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                  {file.name}
                </span>
                <span className="text-xs text-slate-500 flex-shrink-0">
                  ({formatFileSize(file.size)})
                </span>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile(index);
                }}
                disabled={disabled}
                className={cn(
                  "p-1 rounded-full ml-2 flex-shrink-0",
                  "text-slate-500 hover:text-red-500",
                  "hover:bg-red-100 dark:hover:bg-red-900/30",
                  "transition-colors duration-150",
                  disabled && "opacity-50 cursor-not-allowed",
                )}
                aria-label={`Remove ${file.name}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Error message */}
      {hasError && (
        <p
          id={`${name}-error`}
          className="text-red-500 text-xs mt-1"
          role="alert"
        >
          {displayError}
        </p>
      )}
    </div>
  );
};

export default CDropzone;
